import { useNotification } from "@/utils/notification";
import { defineStore } from "pinia";
import { computed, Ref, ref } from "vue";
import { ITableHeader } from "@/types/table";
import { IDriverFilter } from "@/features/drivers/drivers.types";
import { driverService } from "@/features/drivers/drivers.service";

const { pushNotification } = useNotification();

export const useDriverStore = defineStore("driver", () => {
  const commonFilter = ref<IDriverFilter>({
    page: 1,
    per_page: 500,
    search: "",
    provider_id: "",
    company_id: "",
    has_activity_last_2d: null,
    is_active: null,
  });

  const commonSimpleFilter = ref<IDriverFilter>({
    page: 1,
    per_page: 200,
    search: "",
    provider_id: "",
    company_id: "",
    has_activity_last_2d: null,
    is_active: null,
  });

  const clearCommonSimpleFilter = () => {
    commonSimpleFilter.value = {
      page: 1,
      per_page: 200,
      search: "",
      provider_id: "",
      company_id: "",
      has_activity_last_2d: null,
      is_active: null,
    };
  };

  const clearFilter = () => {
    commonFilter.value = {
      page: 1,
      per_page: 500,
      search: "",
      provider_id: "",
      company_id: "",
      has_activity_last_2d: null,
      is_active: null,
    };
  };

  const driverHeaders: ITableHeader[] = [
    {
      name: "id",
      label: "No",
      fitContent: true,
      type: "centert",
    },
    {
      name: "name",
      label: "Drivers",
      width: "25%",
    },
    {
      name: "company_name",
      label: "Company name",
      width: "14%",
    },
    {
      name: "provider_name",
      label: "App name",
      width: "14%",
    },
    {
      name: "open_tasks_count",
      label: "Open tasks",
      width: "10%",
    },
    {
      name: "is_active",
      label: "Status",
      width: "10%",
    },
    {
      name: "eld_last_activity_at",
      label: "Last activity tasks",
      width: "14%",
    },
    {
      name: "actions",
      label: "Actions",
      width: "8%",
    },
  ];

  async function getDrivers(loading: Ref<boolean>) {
    try {
      loading.value = true;
      const { data } = await driverService.getDrivers({
        ...commonFilter.value,
      });
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return [];
    } finally {
      loading.value = false;
    }
  }

  async function getSimpleDrivers(loading: Ref<boolean>) {
    try {
      loading.value = true;
      const { data } = await driverService.getSimpleDrivers({
        ...commonSimpleFilter.value,
      });
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return [];
    } finally {
      loading.value = false;
    }
  }

  async function addDriverNote(
    loading: Ref<boolean>,
    driverId: string,
    payload: { note: string },
    cl: any
  ) {
    loading.value = true;
    await driverService
      .addDriverNote(driverId, payload)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  return {
    commonFilter,
    commonSimpleFilter,
    driverHeaders,
    clearFilter,
    clearCommonSimpleFilter,
    getDrivers,
    getSimpleDrivers,
    addDriverNote,
  };
});
